<template>
  <section :aria-busy="loading" :aria-label="ariaLabel">
    <slot v-if="loading" name="loading-content">
      <ProductCardSkeleton
        v-for="index in perPage"
        :key="`product-loading-${index}`"
      />
    </slot>

    <template v-else>
      <slot
        v-for="(product, index) in products"
        name="product"
        :product="product"
        :loading="loading"
        :index="index"
      >
        <AgsLazy
          :key="`product-lazy-${product.id}`"
          placeholder-ratio="7/13"
          :important="index < priorityCount"
          :ssr="isCrawler"
        >
          <ProductCard
            :key="`product-${product.id}`"
            data-test-id="product-item"
            :product="product"
            :loading="loading"
            :show-price-reduction="isActiveSalePeriod"
            :list-type="listType"
            :list-name="listName"
            :list-index="index"
            :show-lowest-prior-price="showLowestPriorPrice"
            :image="getProductImageSource(product)"
            :hover-image="getProductImage(product.images, '_f')"
            :selected-category="selectedCategory"
            @click:product="$emit('click:product', product, index)"
            @intersect:product="collectIntersection(product, index)"
          >
            <template #header-badge>
              <ProductCardBadges :product="product" />
            </template>
          </ProductCard>
        </AgsLazy>
      </slot>
    </template>

    <slot v-if="!loading && !products?.length" name="no-products-found">
      <p
        class="col-span-full mt-8 text-center text-xl font-medium leading-tight"
      >
        {{ $t('plp.no_products_found') }}
      </p>
    </slot>

    <slot v-else name="listing" />
  </section>
</template>

<script setup lang="ts">
import type { Product, Category } from '@scayle/storefront-nuxt'
import useProductIntersectionCollection from '#layers/base/composables/useProductIntersectionCollection'
import type { ListedProduct, ListItemType } from '~/types/tracking'

const props = withDefaults(
  defineProps<{
    products?: Product[]
    loading?: boolean
    perPage?: number
    selectedCategory?: Category
    showLowestPriorPrice?: boolean
    listType?: ListItemType
    listName?: string
    priorityProductCount?: number
    ariaLabel?: string
  }>(),
  {
    products: () => [],
    loading: false,
    perPage: 24,
    selectedCategory: undefined,
    showLowestPriorPrice: true,
    listType: undefined,
    listName: '',
    priorityProductCount: undefined,
    ariaLabel: undefined,
  },
)

const emit = defineEmits<{
  (e: 'intersect:products', value: ListedProduct[]): void
  (e: 'click:product', value: Product, index: number): void
}>()

const route = useRoute()
const { isMobile } = useDeviceType()
const isCrawler = useNuxtApp().$device.isCrawler

const { isActiveSalePeriod } = useSmartShopConfiguration()

const currentPage = computed(() => parseInt(route.query.page as string) || 1)
const priorityCount = computed(
  () => props.priorityProductCount ?? (isMobile.value ? 4 : 8),
)

const { collectIntersection } = useProductIntersectionCollection({
  perPage: props.perPage,
  currentPage,
  onIntersection: (items) => emit('intersect:products', items),
})

const getProductImageSource = (product: Product) => {
  const isSchuheCategory = product.categories?.some((categoryGroup) =>
    categoryGroup.some((category) => category.categoryId === 473),
  )

  return isSchuheCategory
    ? getImageByAssetType(product.images, '_5') ||
        getImageByAssetType(product.images, '_f') ||
        getFrontImage(product.images)
    : getImageByAssetType(product.images, '_kb') ||
        getProductImage(product.images, '_1')
}
</script>
